/**
 * fnoInterpret — turns raw FNO data into plain-English, decision-ready lines for a
 * GIVEN price level (the crosshair). For each dimension (GEX regime/flip, GEX walls,
 * OI walls, ΔOI, Max Pain, futures, PCR) it returns a short ACTIONABLE outcome plus a
 * one-line EXPLANATION so a newcomer learns how the read was derived. Pure + cheap so
 * it can run on every crosshair move.
 *
 * tone → colour intent: 'good' = supportive / bullish-leaning structure,
 * 'bad' = resistance / bearish / risk, 'neutral' = informational (range/context).
 */

export interface FnoWall { strike: number; gex: number }
export interface FnoOiLevel { strike: number; oi: number | null }
export interface FnoDeltaPoint { strike: number; ceOiChg: number; peOiChg: number }

export interface FnoInterpretInputs {
  spot: number | null;
  gexFlip: number | null;
  netGamma: number | null;
  pcr: number | null;
  maxPain: number | null;
  walls: FnoWall[];                 // latest top ±N γ walls
  support: FnoOiLevel[];            // put-OI walls, ranked by OI desc
  resistance: FnoOiLevel[];         // call-OI walls, ranked by OI desc
  deltaByStrike?: FnoDeltaPoint[];  // ΔOI per strike (from the option profile)
  futures?: { oiPattern: string | null; basis: number; bias: string } | null;
  futuresBuckets?: { price: number; net: number }[];  // futures OI-by-price footprint
  flow?: { score: number; bias: string } | null;       // OVI-lite (option ΔOI flow)
  pinRisk?: { level: string; strike: number } | null;
}

export interface FnoLine {
  dim: string;
  outcome: string;
  explain: string;
  tone: 'good' | 'bad' | 'neutral';
}

const fmtK = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1000) return Math.round(v).toLocaleString('en-US');
  return String(Math.round(v));
};
/** "near" = within this fraction of price (treat as AT the level). */
const NEAR = 0.0015;

export function interpretFnoAtPrice(price: number, inp: FnoInterpretInputs): FnoLine[] {
  const out: FnoLine[] = [];
  if (!(price > 0)) return out;
  const near = (lvl: number) => Math.abs(lvl - price) / price <= NEAR;

  // ── 1. GEX regime + flip — the single most important "what kind of tape" read ──
  if (inp.gexFlip != null && inp.gexFlip > 0) {
    const f = inp.gexFlip;
    if (near(f)) {
      out.push({ dim: 'GEX flip', tone: 'neutral',
        outcome: `At the GEX flip ${fmtK(f)} — regime pivot`,
        explain: 'The zero-gamma line: vol behaviour changes sign here. Wait for which side price holds.' });
    } else if (price > f) {
      out.push({ dim: 'GEX flip', tone: 'neutral',
        outcome: `Above flip ${fmtK(f)} — positive-γ (range / sell premium)`,
        explain: 'Above the flip dealers buy dips & sell rips → moves get dampened; fade extremes.' });
    } else {
      out.push({ dim: 'GEX flip', tone: 'bad',
        outcome: `Below flip ${fmtK(f)} — negative-γ (trend / breakout)`,
        explain: 'Below the flip dealers sell dips & buy rips → moves get amplified; avoid selling premium.' });
    }
  } else if (inp.netGamma != null) {
    const long = inp.netGamma >= 0;
    out.push({ dim: 'GEX regime', tone: long ? 'neutral' : 'bad',
      outcome: long ? 'Net long-γ — range / mean-revert bias' : 'Net short-γ — trend / breakout bias',
      explain: long ? 'Dealers net long gamma dampen the tape — favour premium selling & fading extremes.'
                    : 'Dealers net short gamma amplify the tape — favour debit/spreads & momentum.' });
  }

  // ── 2. GEX walls — pins (+γ) and accelerants (−γ) ──
  if (inp.walls?.length) {
    const pos = inp.walls.filter(w => w.gex > 0).sort((a, b) => Math.abs(a.strike - price) - Math.abs(b.strike - price))[0];
    const neg = inp.walls.filter(w => w.gex < 0).sort((a, b) => Math.abs(a.strike - price) - Math.abs(b.strike - price))[0];
    if (pos) {
      if (near(pos.strike)) {
        out.push({ dim: 'GEX wall', tone: 'neutral',
          outcome: `At +γ wall ${fmtK(pos.strike)} — expect pinning / chop`,
          explain: 'Positive-gamma walls act as magnets; price tends to stall here. Premium-selling favoured.' });
      } else {
        const above = pos.strike > price;
        out.push({ dim: 'GEX wall', tone: above ? 'bad' : 'good',
          outcome: `+γ wall ${fmtK(pos.strike)} ${above ? 'above (magnet/resistance)' : 'below (magnet/support)'}`,
          explain: 'A +γ wall pins price — it tends to gravitate toward it and stall there.' });
      }
    }
    if (neg) {
      out.push({ dim: 'GEX wall', tone: 'neutral',
        outcome: `−γ wall ${fmtK(neg.strike)} — accelerant${near(neg.strike) ? ' (here!)' : ''}`,
        explain: 'Negative-gamma walls speed moves up — a break through one often extends. Breakout trigger.' });
    }
  }

  // ── 3. OI walls (S/R) — biggest call-OI above, biggest put-OI below ──
  const resAbove = (inp.resistance || []).filter(l => l.strike > price).sort((a, b) => a.strike - b.strike)[0];
  const supBelow = (inp.support || []).filter(l => l.strike < price).sort((a, b) => b.strike - a.strike)[0];
  if (resAbove) {
    out.push({ dim: 'OI resistance', tone: 'bad',
      outcome: `Call-OI wall ${fmtK(resAbove.strike)} above — resistance`,
      explain: 'The biggest call open-interest overhead; sellers defend it, so it often caps rallies.' });
  }
  if (supBelow) {
    out.push({ dim: 'OI support', tone: 'good',
      outcome: `Put-OI wall ${fmtK(supBelow.strike)} below — support`,
      explain: 'The biggest put open-interest beneath; writers defend it, so it often cushions dips.' });
  }
  if (resAbove && supBelow) {
    out.push({ dim: 'OI range', tone: 'neutral',
      outcome: `Boxed ${fmtK(supBelow.strike)}–${fmtK(resAbove.strike)}`,
      explain: 'Between the dominant OI walls price tends to range; the edges are the decision points.' });
  } else if (!resAbove && (inp.resistance || []).length) {
    out.push({ dim: 'OI walls', tone: 'good', outcome: 'Above all call-OI walls — open air',
      explain: 'No big call wall overhead → short-covering/breakout room until new OI builds.' });
  } else if (!supBelow && (inp.support || []).length) {
    out.push({ dim: 'OI walls', tone: 'bad', outcome: 'Below all put-OI walls — breakdown risk',
      explain: 'No big put wall beneath → little OI cushion; air-pocket risk until new OI builds.' });
  }

  // ── 4. ΔOI — NET support vs resistance shift at the nearest strike ──
  // net = ΔPE (support change) − ΔCE (resistance change): >0 ⇒ more support added
  // than resistance (bullish); <0 ⇒ more resistance added (bearish).
  if (inp.deltaByStrike?.length) {
    const nearest = inp.deltaByStrike.slice().sort((a, b) => Math.abs(a.strike - price) - Math.abs(b.strike - price))[0];
    if (nearest) {
      const ce = nearest.ceOiChg || 0, pe = nearest.peOiChg || 0;
      const net = pe - ce;
      if (Math.abs(net) > 0) {
        out.push({ dim: 'ΔOI', tone: net >= 0 ? 'good' : 'bad',
          outcome: net >= 0 ? `Net support added @ ${fmtK(nearest.strike)}` : `Net resistance added @ ${fmtK(nearest.strike)}`,
          explain: net >= 0 ? 'Put OI grew more than call OI here — the floor is hardening (bullish).' : 'Call OI grew more than put OI here — the cap is hardening (bearish).' });
      }
    }
  }

  // ── 5. Max Pain — pin pull into expiry ──
  if (inp.maxPain != null && inp.maxPain > 0) {
    const mp = inp.maxPain;
    if (near(mp)) {
      out.push({ dim: 'Max Pain', tone: 'neutral', outcome: `At Max Pain ${fmtK(mp)} — pinned`,
        explain: 'The strike where option buyers lose most; price often gravitates here into expiry.' });
    } else {
      const above = price > mp;
      out.push({ dim: 'Max Pain', tone: 'neutral', outcome: `${above ? 'Above' : 'Below'} Max Pain ${fmtK(mp)} — pin pulls ${above ? 'down' : 'up'}`,
        explain: 'Into expiry price tends to drift toward Max Pain (writers’ least-payout strike).' });
    }
  }

  // ── 6. PCR — writer positioning bias (price-independent context) ──
  if (inp.pcr != null && inp.pcr > 0) {
    const p = inp.pcr;
    if (p >= 1.2) out.push({ dim: 'PCR', tone: 'good', outcome: `PCR ${p.toFixed(2)} — put-heavy (supportive)`, explain: 'More puts than calls written → writers expect support; mildly bullish.' });
    else if (p < 0.8) out.push({ dim: 'PCR', tone: 'bad', outcome: `PCR ${p.toFixed(2)} — call-heavy (resistive)`, explain: 'More calls than puts written → writers expect a cap; mildly bearish.' });
    else out.push({ dim: 'PCR', tone: 'neutral', outcome: `PCR ${p.toFixed(2)} — balanced`, explain: 'Put/call open-interest roughly even → no strong writer lean.' });
  }

  // ── 7. Futures — OI build pattern + basis (price-independent context) ──
  if (inp.futures && inp.futures.oiPattern) {
    const pat = String(inp.futures.oiPattern).toUpperCase();
    const map: Record<string, { t: 'good' | 'bad' | 'neutral'; s: string; e: string }> = {
      LONG_BUILD: { t: 'good', s: 'Futures: longs building — uptrend', e: 'Price up + OI up = fresh longs; trend-up confirmation.' },
      SHORT_COVER: { t: 'good', s: 'Futures: short-covering — squeeze up', e: 'Price up + OI down = shorts covering; up-move but maturing.' },
      SHORT_BUILD: { t: 'bad', s: 'Futures: shorts building — downtrend', e: 'Price down + OI up = fresh shorts; trend-down confirmation.' },
      LONG_UNWIND: { t: 'bad', s: 'Futures: longs unwinding — weakness', e: 'Price down + OI down = longs exiting; up-move losing fuel.' },
    };
    const m = map[pat];
    const basis = Number(inp.futures.basis || 0);
    const basisTxt = basis ? ` · ${basis > 0 ? 'premium' : 'discount'} ${Math.abs(basis).toFixed(2)}` : '';
    if (m) out.push({ dim: 'Futures', tone: m.t, outcome: m.s + basisTxt, explain: m.e });
  }

  // ── 8. Futures OI-by-price footprint at this level ──
  if (inp.futuresBuckets?.length) {
    const b = inp.futuresBuckets.slice().sort((a, c) => Math.abs(a.price - price) - Math.abs(c.price - price))[0];
    if (b && Math.abs(b.net) > 0) {
      out.push({ dim: 'Fut ΔOI', tone: b.net >= 0 ? 'neutral' : 'neutral',
        outcome: `Futures OI ${b.net >= 0 ? 'built' : 'unwound'} near ${fmtK(b.price)}`,
        explain: b.net >= 0 ? 'Fresh futures positions accumulated here — an active battle level.' : 'Futures positions were cut here — prior interest is leaving.' });
    }
  }

  return out;
}

/** clamp helper */
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/**
 * scoreFno — a single Bull/Bear score (-100..+100) at a price, blending every FNO
 * dimension. Each component yields a signed lean in [-1,1] with a weight; the score
 * is the weighted average over the components that HAVE data (missing data never
 * skews it) × 100. Used for the crosshair Bull/Bear rank and the OverView meter.
 */
export function scoreFno(price: number, inp: FnoInterpretInputs): number {
  if (!(price > 0)) return 0;
  let acc = 0, wsum = 0;
  const push = (lean: number, w: number) => { acc += clamp(lean, -1, 1) * w; wsum += w; };

  // 1) OI-wall position — room up vs room down + relative wall size.
  const resAbove = (inp.resistance || []).filter(l => l.strike > price).sort((a, b) => a.strike - b.strike)[0];
  const supBelow = (inp.support || []).filter(l => l.strike < price).sort((a, b) => b.strike - a.strike)[0];
  if (resAbove && supBelow) {
    const ru = (resAbove.strike - price) / price, rd = (price - supBelow.strike) / price;
    let lean = (ru - rd) / ((ru + rd) || 1);
    const so = Number(supBelow.oi) || 0, ro = Number(resAbove.oi) || 0;
    if (so + ro > 0) lean = 0.6 * lean + 0.4 * ((so - ro) / (so + ro));
    push(lean, 26);
  } else if (resAbove) push(-0.5, 26);
  else if (supBelow) push(0.5, 26);
  else if ((inp.resistance || []).length) push(0.7, 26);
  else if ((inp.support || []).length) push(-0.7, 26);

  // 2) GEX flip side (mild — regime, not pure direction).
  if (inp.gexFlip != null && inp.gexFlip > 0) push(clamp((price - inp.gexFlip) / (inp.gexFlip * 0.01), -1, 1) * 0.5, 8);

  // 3) Max Pain pull.
  if (inp.maxPain != null && inp.maxPain > 0) push(clamp((inp.maxPain - price) / (price * 0.015), -1, 1), 12);

  // 4) PCR.
  if (inp.pcr != null && inp.pcr > 0) push(clamp((inp.pcr - 1) / 0.4, -1, 1), 12);

  // 5) ΔOI near price (put-writing/call-unwind bullish; call-writing/put-unwind bearish).
  if (inp.deltaByStrike?.length) {
    const n = inp.deltaByStrike.slice().sort((a, b) => Math.abs(a.strike - price) - Math.abs(b.strike - price))[0];
    if (n) {
      const ce = n.ceOiChg || 0, pe = n.peOiChg || 0;
      const bull = Math.max(0, pe) + Math.max(0, -ce);
      const bear = Math.max(0, -pe) + Math.max(0, ce);
      if (bull + bear > 0) push((bull - bear) / (bull + bear), 12);
    }
  }

  // 6) Futures OI pattern + basis.
  if (inp.futures && inp.futures.oiPattern) {
    const pat = String(inp.futures.oiPattern).toUpperCase();
    let lean = pat === 'LONG_BUILD' || pat === 'SHORT_COVER' ? 1 : pat === 'SHORT_BUILD' || pat === 'LONG_UNWIND' ? -1 : 0;
    const basis = Number(inp.futures.basis || 0);
    if (basis) lean = clamp(lean + (basis > 0 ? 0.2 : -0.2), -1, 1);
    if (lean !== 0) push(lean, 18);
  }

  // 7) Option flow (OVI).
  if (inp.flow && Number.isFinite(inp.flow.score)) push(clamp(inp.flow.score, -1, 1), 12);

  if (wsum <= 0) return 0;
  return Math.round(clamp((acc / wsum) * 100, -100, 100));
}
